import {ChangeEvent, forwardRef, KeyboardEvent, useEffect, useRef, useState} from "react";
import {FiCalendar} from "react-icons/fi";
import {useDebouncedCallback} from "use-debounce";

import {formatInputDate, parseToAccountTimeZone} from "@/components/datePicker/DatePicker.utils.ts";
import {DatePickerPopoverSingleDateInputProps} from "@/components/datePicker/subComponents/DatePickerPopoverSingleDateInput/DatePickerPopoverSingleDateInput.types.ts";
import {datePickerDefaultInputValidators} from "@/components/datePicker/subComponents/DatePickerPopoverSingleDateInput/DatePickerPopoverSingleDateInput.utils.ts";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Input} from "@/components/input/Input.tsx";
import {InputGroup} from "@/components/inputGroup/InputGroup.tsx";

// Delay before an in-progress typed value is committed up to the picker, so we
// don't validate/commit on every keystroke while the user is still typing.
const COMMIT_DEBOUNCE_MS = 400;

export const DatePickerPopoverSingleDateInput = forwardRef<HTMLInputElement, DatePickerPopoverSingleDateInputProps>(
  ({placeholder = "Select...", validators, onChange, dateFormat, timeFormat, initialDate, timezone, ...rest}, ref) => {
    validators = [...datePickerDefaultInputValidators, ...(validators || [])];
    const [inputValue, setInputValue] = useState(
      initialDate ? formatInputDate(initialDate, dateFormat, timeFormat, timezone) : ""
    );
    const [isValid, setIsValid] = useState(true);
    const [errorMessageIndex, setErrorMessageIndex] = useState<number>(0);
    // Ref (not state) so the sync effect below can check "am I typing?" without
    // re-running on blur — running it on blur would overwrite an invalid entry
    // with the previous value and hide its validation message.
    const isEditingRef = useRef(false);

    useEffect(() => {
      // Don't overwrite what the user is actively typing (the debounced commit
      // round-trips through `initialDate`). External changes to the committed
      // value still sync while the field isn't focused.
      if (isEditingRef.current) return;
      setInputValue(initialDate ? formatInputDate(initialDate, dateFormat, timeFormat, timezone) : "");
      setIsValid(true);
    }, [initialDate, dateFormat, timeFormat, timezone]);

    // Commit the current input value up to the picker. `reformat` rewrites the
    // field to the canonical format — only wanted on blur/Enter, not mid-typing.
    const commitInput = (value: string, reformat: boolean) => {
      if (!value) {
        setIsValid(true);
        onChange?.("", undefined);
        return;
      }
      const date = parseToAccountTimeZone(value, timezone);
      for (const [index, {validate}] of validators.entries()) {
        if (!validate(value)) {
          setIsValid(false);
          setErrorMessageIndex(index);
          return;
        }
      }
      if (date) {
        const formattedDate = formatInputDate(date, dateFormat, timeFormat, timezone);
        onChange?.(reformat ? formattedDate : value, date);
        if (reformat) setInputValue(formattedDate);
        setIsValid(true);
      }
    };

    const debouncedCommit = useDebouncedCallback((value: string) => commitInput(value, false), COMMIT_DEBOUNCE_MS);

    // Cancel any pending debounced commit when the field unmounts (e.g. the picker
    // closes) so we don't update state on an unmounted tree.
    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setIsValid(true);
      debouncedCommit(e.target.value);
    };

    const handleBlur = () => {
      isEditingRef.current = false;
      debouncedCommit.cancel();
      commitInput(inputValue, true);
    };

    const handleFocus = () => {
      isEditingRef.current = true;
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        debouncedCommit.cancel();
        commitInput(inputValue, true);
        e.currentTarget.blur();
      }
    };

    return (
      <div className={"w-100"}>
        <InputGroup endElement={<Icon icon={FiCalendar} />}>
          <Input
            ref={ref}
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            aria-invalid={!isValid}
            {...rest}
          />
        </InputGroup>
        <FormSubtext
          error={
            !isValid
              ? errorMessageIndex in validators
                ? validators[errorMessageIndex].errorMessage
                : "Something Went Wrong"
              : ""
          }
        />
      </div>
    );
  }
);
